import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StarIcon from "@material-ui/icons/Star";
import HelpIcon from "@material-ui/icons/Help";

import Home from "../Home/Home";
import DeleteDialog from "../../components/Reusable/DeleteDialogGeneric";
import {
  getAvatars,
  getStockFaces,
  syncAvatar,
  setDefaultAvatar,
  deleteAvatar,
} from "../../Redux/Actions/agent";
import { selectAvatars } from "../../Redux/Selectors";
import { Avatar } from "../../Redux/Sagas/agent/api";
import StockFacePicker from "./StockFacePicker";
import DigitalTwinForm from "./DigitalTwinForm";
import "../ai-shared.css";

const POLL_MS = 30000;

const STATUS: Record<string, { label: string; tone: string }> = {
  training: { label: "Training", tone: "working" },
  ready: { label: "Ready", tone: "ok" },
  failed: { label: "Failed", tone: "danger" },
};

const SOURCE_LABEL: Record<string, string> = {
  stock: "Stock presenter",
  video: "Digital twin",
};

const Avatars: React.FC = () => {
  const dispatch = useDispatch();
  const avatars: Avatar[] = useSelector(selectAvatars);
  const loading: boolean = useSelector((state: any) => state.agent.avatarsLoading);
  const error: string | null = useSelector((state: any) => state.agent.avatarsError);
  const pending: Record<string, string> = useSelector((state: any) => state.agent.avatarPending);
  const [confirmDelete, setConfirmDelete] = useState<Avatar | null>(null);

  useEffect(() => {
    dispatch(getAvatars());
    dispatch(getStockFaces());
  }, [dispatch]);

  // Poll avatars that are still training.
  const trainingIds = avatars
    .filter((avatar) => avatar.status === "training")
    .map((avatar) => avatar.id)
    .join(",");
  useEffect(() => {
    if (!trainingIds) return undefined;
    const timer = window.setInterval(() => {
      trainingIds.split(",").forEach((id) => dispatch(syncAvatar(id)));
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [trainingIds, dispatch]);

  const deleting = Boolean(confirmDelete && pending[confirmDelete.id] === "delete");
  useEffect(() => {
    // Close the dialog once the avatar is gone (or the delete failed).
    if (confirmDelete && !pending[confirmDelete.id] && !avatars.some((a) => a.id === confirmDelete.id)) {
      setConfirmDelete(null);
    }
  }, [avatars, pending, confirmDelete]);

  const renderAvatar = (avatar: Avatar) => {
    const status = STATUS[avatar.status] || STATUS.training;
    const busy = Boolean(pending[avatar.id]);
    return (
      <div className="avatarCard" key={avatar.id}>
        <div className="avatarCardTop">
          {avatar.thumbnailUrl ? (
            <img className="avatarThumb" src={avatar.thumbnailUrl} alt={avatar.name} />
          ) : (
            <div className="avatarThumb" />
          )}
          <div style={{ minWidth: 0 }}>
            <div className="avatarName">
              {avatar.name}
              {avatar.isDefault && (
                <Tooltip title="Default avatar" arrow>
                  <StarIcon fontSize="small" className="avatarDefaultIcon" />
                </Tooltip>
              )}
            </div>
            <div className="aiChips">
              <span className={`aiChip aiChip--${status.tone}`}>{status.label}</span>
              <span className="aiMuted">{SOURCE_LABEL[avatar.source] || avatar.source}</span>
            </div>
            {avatar.status === "training" && (
              <div className="aiMuted" style={{ marginTop: 6 }}>
                Training can take a few hours. This page checks every 30 seconds.
              </div>
            )}
            {avatar.error && (
              <div className="aiErrorText" style={{ marginTop: 6 }}>
                {avatar.error}
              </div>
            )}
          </div>
        </div>
        <div className="aiActions">
          {avatar.status === "training" && (
            <Button
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => dispatch(syncAvatar(avatar.id))}
            >
              Check status
            </Button>
          )}
          {avatar.status === "ready" && !avatar.isDefault && (
            <Button
              size="small"
              startIcon={<CheckCircleIcon fontSize="small" />}
              disabled={busy}
              onClick={() => dispatch(setDefaultAvatar(avatar.id))}
            >
              {pending[avatar.id] === "default" ? "Saving..." : "Make default"}
            </Button>
          )}
          <Button
            size="small"
            className="aiDangerText"
            startIcon={<DeleteIcon fontSize="small" />}
            disabled={busy}
            onClick={() => setConfirmDelete(avatar)}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Home>
      <div className="aiPage">
        <div className="aiHeader">
          <div>
            <span className="heading">Video Avatars </span>
            <span className="headingTooltip">
              <Tooltip
                title="Avatars present your AI video replies. Pick a stock presenter, or create a digital twin of yourself from a short training video."
                placement="right-start"
                arrow
              >
                <HelpIcon />
              </Tooltip>
            </span>
            <div className="aiSub">
              {avatars.length} avatar{avatars.length === 1 ? "" : "s"}
            </div>
          </div>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => dispatch(getAvatars())}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>

        <div className="aiCard">
          <div className="aiCardTitle">Your avatars</div>
          {loading && avatars.length === 0 ? (
            <div className="aiLoading">
              <CircularProgress />
            </div>
          ) : error && avatars.length === 0 ? (
            <div className="aiNotice aiNotice--error">
              Could not load your avatars: {error}{" "}
              <Button size="small" onClick={() => dispatch(getAvatars())}>
                Try again
              </Button>
            </div>
          ) : avatars.length === 0 ? (
            <div className="aiMuted">No avatars yet. Add a stock presenter or create your twin below.</div>
          ) : (
            <div className="avatarList">{avatars.map(renderAvatar)}</div>
          )}
        </div>

        <div className="aiGrid">
          <StockFacePicker />
          <DigitalTwinForm />
        </div>

        <DeleteDialog
          open={Boolean(confirmDelete)}
          isDeleting={deleting}
          closeDeleteDialog={() => setConfirmDelete(null)}
          actionDelete={() => confirmDelete && dispatch(deleteAvatar(confirmDelete.id))}
          textContent={
            confirmDelete
              ? `Remove the avatar "${confirmDelete.name}"? This cannot be undone.`
              : ""
          }
        />
      </div>
    </Home>
  );
};

export default Avatars;
